<template>
  <div class="candidates">
    <page-header
      :title="pageTitle"
      :subtitle="`${totals} results found`"
    >
      <template #action>
        <v-btn
          :to="{ name: 'hr-recruitment-show',
            params: { projectId: projectId, recruitmentId: recruitmentId }
          }"
          link
          color="button"
          class="fw-400"
        >
          BACK
        </v-btn>
      </template>
      <template #breadcrumbs>
        <router-link :to="{ name: 'hr-projects' }">
          Projects
        </router-link>
        <span>/</span>
        <router-link :to="{ name: 'hr-project-show', params: { id: projectId } }">
          {{ navigation.project.name }}
        </router-link>
        <span>/</span>
        <router-link :to="{ name: 'hr-recruitment-show',
          params: { projectId: projectId, recruitmentId: recruitmentId }}"
        >
          {{ navigation.recruitment.name }}
        </router-link>
        <span>/ Candidates</span>
      </template>
    </page-header>

    <MultiSelectSearch
      :features="featuresFilters"
      :selected-features="selectedFeatures"
      @search="searchDevelopers"
    />

    <div class="candidates__table data-table">
      <v-data-table
        :headers="candidatesTableHeaders"
        :items="candidatesList"
        :options.sync="paginationOptions"
        :server-items-length="totals"
        :show-arrows="true"
        :footer-props="{
          'items-per-page-options': [10, 20, 50, 100],
          'next-icon': 'mdi-menu-right',
          'prev-icon': 'mdi-menu-left',
          'disable-pagination': isDataLoading,
        }"
        :loading="isDataLoading"
        multi-sort
        no-data-text="No data"
        no-results-text="No data"
      >
        <template #item.add="{ item }">
          <span>
            <v-btn v-if="!item.draft" color="blue-light" small depressed
              class="candidates__btn candidates__btn--add"
              @click="addToDraft(item.developer_id)">
              <v-icon color="blue" size="14">mdi-plus</v-icon>
            </v-btn>
            <v-btn v-else color="blue" small depressed
              class="candidates__btn candidates__btn--check"
              @click="deleteFromDraft(item.developer_id)">
              <v-icon color="white" size="10">mdi-check-bold</v-icon>
            </v-btn>
          </span>
        </template>

        <template #item.full_name="{ item }">
          <router-link
            :to="{ name: 'hr-developer-show', params: { id: item.developer_id } }"
            class="data-table__link"
          >
            <v-avatar
              size="32px"
              color="blue-light"
              class="mr-3"
            >
              <img
                v-if="item.avatar"
                alt="Avatar"
                src="item.avatar"
              >
              <span v-else class="white--text">
                {{ nameInitials(item.full_name) }}
              </span>
            </v-avatar>
            {{ item.full_name }}
          </router-link>
        </template>

        <template #item.score="{ value }">
          <DeveloperScore :score="value" />
        </template>

        <template #item.level="{ value }">
          <span>{{ value }}</span>
        </template>

        <template #item.top_skills="{ value }">
          <span>
            <DataTableTooltipedCell :elements="value" />
          </span>
        </template>

        <template #item.rate="{ value }">
          <span>{{ value }} / h</span>
        </template>

        <template #item.status="{ value }">
          {{ value }}
        </template>

        <template #item.availability="{ value }">
          <DeveloperAvailabilityStatus :availability="value" />
        </template>

        <template #item.action="{ item }">
          <v-menu left min-width="100">
            <template v-slot:activator="{ on }">
              <v-btn icon color="secondary" v-on="on">
                <v-icon large>mdi-dots-horizontal</v-icon>
              </v-btn>
            </template>

            <v-list dense elevation-1>
              <v-list-item
                link
                :to="{ name: 'hr-developer-show', params: { id: item.developer_id } }"
              >
                <v-list-item-title>Edit</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </v-data-table>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { candidatesTableHeaders } from '@/helpers/tableHeaders';
import PageHeader from '@/components/PageHeader.vue';
import DeveloperAvailabilityStatus from '@/components/Developers/DeveloperAvailabilityStatus.vue';
import DeveloperScore from '@/components/Developers/DeveloperScore.vue';
import DataTableTooltipedCell from '@/components/Developers/DataTableTooltipedCell.vue';
import MultiSelectSearch from '@/components/MultiSelectSearch.vue';

export default {
  metaInfo() {
    return {
      title: `Candidates - ${this.navigation.recruitment.name}`,
    };
  },

  components: {
    PageHeader,
    DeveloperAvailabilityStatus,
    DataTableTooltipedCell,
    DeveloperScore,
    MultiSelectSearch,
  },

  props: {
    projectId: {
      type: [String, Number],
      required: true,
    },
    recruitmentId: {
      type: [String, Number],
      required: true,
    },
  },

  data() {
    return {
      candidatesTableHeaders,
      pageTitle: 'Featured developers',
    };
  },

  computed: {
    ...mapState('hrCandidates', [
      'candidatesList',
      'totals',
      'pagination',
      'navigation',
      'selectedFeatures',
      'isDataLoading',
    ]),

    ...mapState('features', [
      'featuresFilters',
    ]),

    paginationOptions: {
      get() {
        return this.pagination;
      },
      set(val) {
        this.setCandidatesPagination(val);
      },
    },
  },

  watch: {
    paginationOptions: {
      handler() {
        this.fetchCandidates(this.recruitmentId);
      },
      deep: true,
    },
  },

  created() {
    this.fetchFeatures();
    this.fetchCandidates(this.recruitmentId);
  },

  methods: {
    ...mapActions('hrCandidates', [
      'fetchCandidates',
      'setCandidatesPagination',
      'createCandidate',
      'deleteCandidate',
      'setSelectedFeatures',
    ]),

    ...mapActions('features', [
      'fetchFeatures',
    ]),

    addToDraft(id) {
      const candidateParams = {
        developer_id: id,
        recruitment_id: this.recruitmentId,
      };

      this.createCandidate(candidateParams)
        .then(() => {
          this.fetchCandidates(this.recruitmentId);
        });
    },

    deleteFromDraft(id) {
      const candidateParams = {
        developer_id: id,
        recruitment_id: this.recruitmentId,
      };

      this.deleteCandidate(candidateParams)
        .then(() => {
          this.fetchCandidates(this.recruitmentId);
        });
    },

    searchDevelopers(params) {
      this.setSelectedFeatures(params);
      this.fetchCandidates(this.recruitmentId);
    },

    nameInitials(name) {
      return name.split(' ').map((n) => n[0]).join('').substr(0, 2);
    },
  },
};
</script>

<style lang="scss" scoped>
.candidates {
  &__btn {
    width: 20px !important;
    max-width: 20px !important;
    min-width: unset !important;
    height: 20px !important;
    border-radius: 8px;
    padding: 0  !important;
  }
}
.data-table {
  padding: 12px 16px;
  background: #FFFFFF;
  box-shadow: 0px 3px 6px #00000029;
  border-radius: 8px;

  &__link {
    text-decoration: unset !important;
    display: flex;
    align-items: center;
  }

  &__name {
    max-width: 364px;
    word-break: break-word;
  }

  .v-btn:not(.v-btn--round).v-size--default {
    height: 36px;
    min-width: 64px;
    padding: 0 24px;
  }
}
</style>
